import { Component, ElementRef, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from 'src/app/@shared/services/common.service';
import { EncryptionService } from 'src/app/@shared/services/encryptionService/encryption.service';
import { MiscellaneousService } from 'src/app/@shared/services/service/miscellaneous.service';
import { BgvService } from 'src/app/@shared/services/service/recruitment/bgv.service';
import { OfferLetterService } from 'src/app/@shared/services/service/recruitment/offer-letter.service';
import { environment } from 'src/app/environments/environment';

@Component({
  standalone: false,
  selector: 'app-candidate-upload-doc',
  templateUrl: './candidate-upload-doc.component.html',
  styleUrls: ['./candidate-upload-doc.component.scss'],
})
export class CandidateUploadDocComponent implements OnDestroy {
  recordData: any = {};
  dataList: any = [];
  baseUrl: any;
  address: any;
  private objectUrls = new Set<string>();

  formObj: any = {
    document_details: [
      {
        document_name: '',
        document_number: '',
        image: null,
        document_path: null,
        files: [],
      },
    ],
  };

  candidate_id: any;
  fileControls: any = [];
  /** Pending single-file edit info (set on Edit click, applied on next upload). */
  pendingEdit:
    | {
        rowIndex: number;
        fileIndex: number;
        isPath: boolean;
      }
    | null = null;
  /** Set true on submit so error borders show only after user tries to submit. */
  submitAttempted = false;

  @ViewChildren('docRow') docRows!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('addressField') addressField!: ElementRef<HTMLElement>;

  constructor(
    private toast: ToastrService,
    private activeroute: ActivatedRoute,
    private $offerLetter: OfferLetterService,
    private _bgvService: BgvService,
    private $miscellaneousService: MiscellaneousService,
    private $common: CommonService,
    private route: Router,
    private crypto: EncryptionService
  ) {
    this.baseUrl = environment.servralUrl;
  }

  ngOnInit() {
    this.activeroute.queryParams.subscribe((params: any) => {
      if (!params?.id) {
        return;
      }
      try {
        const decryptedId = this.crypto.decryptObject(params.id);
        this.candidate_id = Number(decryptedId);
        if (this.candidate_id) {
          this.getCandidateoffer_detailsBYID();
        }
      } catch (error) {
        console.error('Invalid encrypted candidate id');
      }
    });
  }

  getCandidateoffer_detailsBYID() {
    try {
      this.$offerLetter
        .getcandiate_detailsBYID(this.candidate_id)
        .subscribe((res) => {

          this.recordData = res?.data[0];
          this.address = this.recordData?.address;

          if (this.recordData?.candidate_document_checklist) {
            const parsed = this.parseDocumentChecklist(this.recordData.candidate_document_checklist);
            this.formObj.document_details = parsed.map((item: { document: string; required: boolean }) => ({
              document_name: item.document,
              document_number: '',
              image: null,
              document_path: null,
              files: [],
              isFromChecklist: true,
              required: item.required !== false
            }));
          }

          // After checklist rows are set, merge in existing uploaded documents from API.
          this.loadExistingCandidateDocuments();
        });
    } catch (err) {
      
    }
  }

  /** Fetch already uploaded candidate documents and merge into document_details. */
  loadExistingCandidateDocuments() {
    if (!this.candidate_id) {
      return;
    }
    try {
      this._bgvService
        .get_candidate_documents_by_candidate_id(this.candidate_id)
        .subscribe(
          (res: any) => {
            const list = Array.isArray(res?.data) ? res.data : [];
            if (!list.length) {
              return;
            }
            list.forEach((item: any) => {
              const name = item?.document_name || item?.document || '';
              if (!name) {
                return;
              }
              const number = item?.document_number || '';
              const path = item?.document_path || '';

              // Try to merge into an existing checklist row with the same document name.
              const existingIndex = this.formObj.document_details.findIndex(
                (d: any) => (d?.document_name || '').toLowerCase() === String(name).toLowerCase()
              );
              if (existingIndex !== -1) {
                const doc = this.formObj.document_details[existingIndex];
                doc.document_number = doc.document_number || number;
                doc.document_path = path || doc.document_path;
                doc.isFromApi = true;
              } else {
                this.formObj.document_details.push({
                  document_name: name,
                  document_number: number,
                  image: null,
                  document_path: path || null,
                  files: [],
                  isFromChecklist: false,
                  isFromApi: true,
                  required: false,
                });
              }
            });
          },
          () => {
            // ignore fetch errors, user can still upload new documents
          }
        );
    } catch {
      // no-op
    }
  }

  handleSubmit() {
    try {
      this.submitAttempted = true;

      const hasFiles = (d: any) => (d?.files?.length > 0) || !!d?.document_path;
      const invalid = this.formObj.document_details.some((doc: any) => {
        if (doc.required && (!doc.document_number || !hasFiles(doc))) {
          return true;
        }
        if (doc.document_number && !hasFiles(doc)) {
          return true;
        }
        if (hasFiles(doc) && !doc.document_number) {
          return true;
        }
        return false;
      });

      if (invalid) {
        this.toast.error(
          "Required document rows must have Document Number and File. For optional rows, if you enter number or upload file, both are required.",
          "Validation Error"
        );
        this.scrollToFirstError();
        return;
      }

      if (!this.address) {
        this.toast.error("Please fill your address", "Validation Error");
        this.scrollToFirstError();
        return;
      }

      const formData = new FormData();
      formData.append('candidate_id', String(this.candidate_id));
      formData.append('address', this.address);
      const document_details = this.formObj.document_details.map((doc: any) => ({
        document_name: doc.document_name,
        document_number: doc.document_number,
        document_path: doc.document_path || '',
        file_count: (doc.files && doc.files.length) || 0,
      }));
      formData.append('document_details', JSON.stringify(document_details));
      this.formObj.document_details.forEach((doc: any) => {
        const files = doc.files || [];
        files.forEach((file: File) => formData.append('file', file));
      });

      this._bgvService.upload_candidate_documents(formData).subscribe(
        (res) => {
          this.route.navigateByUrl('/auth/pop-up-manpower/external-job-apply');
          this.toast.success(res.message);
          this.formObj = {
            document_details: [
              {
                document_name: '',
                document_number: '',
                image: null,
                document_path: null,
                files: [],
              },
            ],
          };
        },
        (error) => {
          if (error.error.code == 403) this.toast.error(error.error.message);
        }
      );
    } catch (err) {
      
    }
  }

  uploadedFileUrls: string[] = [];

  /** Get files for display (binary upload: we store File objects). */
  getFiles(doc: any): File[] {
    return doc?.files ? Array.from(doc.files) : [];
  }

  /** Get paths for display (when loaded from server / legacy). */
  getPaths(doc: any): string[] {
    if (!doc?.document_path) return [];
    return doc.document_path.split(',').map((p: string) => p.trim()).filter(Boolean);
  }

  /** Open a selected File in new tab (using object URL). */
  openFileInNewTab(file: File): void {
    try {
      const url = URL.createObjectURL(file);
      this.objectUrls.add(url);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      this.toast.error('Could not open file');
    }
  }

  /** Open server path in new tab. */
  openPathInNewTab(path: string): void {
    const url = path.startsWith('http') ? path : `${this.baseUrl}/${path}`;
    window.open(url, '_blank', 'noopener');
  }

  ngOnDestroy(): void {
    this.objectUrls.forEach(u => URL.revokeObjectURL(u));
    this.objectUrls.clear();
  }

  uploadDoc(event: Event, rowIndex: number) {
    try {
      const inputElement = event.target as HTMLInputElement;
      if (!inputElement.files || !inputElement.files.length) return;
      const files = Array.from(inputElement.files);
      const maxSize = 5 * 1024 * 1024; // 5MB
      for (const file of files) {
        if (file.size > maxSize) {
          this.toast.error('File size should not exceed 5MB');
          inputElement.value = '';
          return;
        }
      }
      const isValidExtension = this.$common.checkForValidFile(event, false, true, false);
      if (!isValidExtension) {
        if (this.fileControls[rowIndex]) this.fileControls[rowIndex].fileUrl = '';
        inputElement.value = '';
        return;
      }
      const doc = this.formObj.document_details[rowIndex];
      if (!doc.files) doc.files = [];

      // If there is a pending single-file edit for this row, first remove that specific entry.
      if (
        this.pendingEdit &&
        this.pendingEdit.rowIndex === rowIndex
      ) {
        const { fileIndex, isPath } = this.pendingEdit;

        if (isPath) {
          const paths = this.getPaths(doc);
          if (fileIndex >= 0 && fileIndex < paths.length) {
            paths.splice(fileIndex, 1);
            doc.document_path = paths.length ? paths.join(',') : null;
          }
        } else {
          if (doc.files && fileIndex >= 0 && fileIndex < doc.files.length) {
            doc.files.splice(fileIndex, 1);
          }
        }

        this.pendingEdit = null;
      }

      doc.files.push(...files);
      inputElement.value = '';
    } catch (error) {
      // no-op
    }
  }

  removeSpaces(event: any) {
    const trimmed = event.target.value.replace(/^\s+/, '');
    if (event.target.value !== trimmed) {
      event.target.value = trimmed;
      event.target.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  removeFileControl(index: number) {

    if (this.formObj.document_details[index].isFromChecklist) {
      return;
    }

    this.formObj.document_details.splice(index, 1);
  }


  clearData(index: number, input: HTMLInputElement) {
    const doc = this.formObj.document_details[index];
    doc.document_number = '';
    doc.document_path = null;
    doc.files = [];
    doc.image = null;
    input.value = '';
  }

  /** Edit a single file/path: mark it for replacement and open chooser. */
  editFile(rowIndex: number, fileIndex: number, isPath: boolean, input: HTMLInputElement) {
    const doc = this.formObj.document_details[rowIndex];
    if (!doc) {
      return;
    }
    this.pendingEdit = { rowIndex, fileIndex, isPath };
    input.value = '';
    input.click();
  }

  /** Parse candidate_document_checklist: supports [{ document, required }][] or string[] (legacy). */
  parseDocumentChecklist(checklist: any): Array<{ document: string; required: boolean }> {
    if (!checklist) return [];
    let arr: any[] = [];
    if (typeof checklist === 'string') {
      try {
        let cleaned = checklist.trim();
        if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
          cleaned = cleaned.substring(1, cleaned.length - 1);
        }
        const parsed = JSON.parse(cleaned);
        arr = Array.isArray(parsed) ? parsed : [];
      } catch {
        arr = checklist.includes(',') ? checklist.split(',').map((s: string) => s.trim()).filter(Boolean) : (checklist.trim() ? [checklist.trim()] : []);
      }
    } else if (Array.isArray(checklist)) {
      arr = checklist;
    }
    if (arr.length === 0) return [];
    const first = arr[0];
    if (first != null && typeof first === 'object' && 'document' in first) {
      return arr.map((o: any) => ({
        document: String(o?.document ?? o?.name ?? ''),
        required: o?.required !== false
      })).filter((o) => o.document);
    }
    return arr.map((s: any) => ({ document: String(s), required: true }));
  }

  addFileControl(insertAfterIndex: number) {
    const newRow = {
      document_name: '',
      document_number: '',
      image: null,
      document_path: null,
      files: [] as File[],
      isFromChecklist: false,
      required: false
    };
    const at = insertAfterIndex + 1;
    this.formObj.document_details.splice(at, 0, newRow);
  }

  preventLeadingSpace(event: KeyboardEvent, value: string) {
    if (value.length === 0 && event.key === ' ') {
      event.preventDefault();
    }
  }

  /** Remove one file from a row: index 0..files.length-1 = doc.files, rest = paths. */
  removeUploadedFile(rowIndex: number, fileIndex: number) {
    const doc = this.formObj.document_details[rowIndex];
    const fileCount = (doc.files && doc.files.length) || 0;
    if (fileIndex < fileCount) {
      doc.files.splice(fileIndex, 1);
      doc.image = null;
      return;
    }
    const paths = this.getPaths(doc);
    const pathIndex = fileIndex - fileCount;
    if (pathIndex >= 0 && pathIndex < paths.length) {
      paths.splice(pathIndex, 1);
      doc.document_path = paths.length ? paths.join(',') : null;
      doc.image = null;
    }
  }

  hasFiles(doc: any): boolean {
    return (doc?.files?.length > 0) || !!doc?.document_path;
  }

  /** Returns error message for this document row if invalid, else null. Matches handleSubmit validation. */
  getRowError(doc: any): string | null {
    if (!doc) return null;
    const hasFiles = (d: any) => (d?.files?.length > 0) || !!d?.document_path;
    if (doc.required && (!doc.document_number || !hasFiles(doc))) return 'Required: Document Number and File';
    if (doc.document_number && !hasFiles(doc)) return 'Document Number requires a file';
    if (hasFiles(doc) && !doc.document_number) return 'File requires Document Number';
    return null;
  }

  /** Index of first invalid document row, or -1. */
  getFirstInvalidRowIndex(): number {
    const list = this.formObj?.document_details || [];
    for (let i = 0; i < list.length; i++) {
      if (this.getRowError(list[i])) return i;
    }
    return -1;
  }

  /** True when address is shown but empty. */
  hasAddressError(): boolean {
    return !this.recordData?.address && (this.address == null || String(this.address).trim() === '');
  }

  /** Scroll to first invalid element (address or document row) after validation fails. */
  scrollToFirstError(): void {
    setTimeout(() => {
      if (this.hasAddressError() && this.addressField?.nativeElement) {
        this.addressField.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const idx = this.getFirstInvalidRowIndex();
      if (idx >= 0 && this.docRows?.length > idx) {
        const el = this.docRows.get(idx)?.nativeElement;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  isImage(path: string): boolean {
  if (!path) return false;

  const extension = path.split('.').pop()?.toLowerCase();

  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '');
}

}
